import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import Logo from "../components/Logo";
import { requestFcmToken } from "../lib/firebase";
import { registerNativePush } from "../lib/native";
import { isNativeApp, nativePlatform } from "../lib/platform";
import { isPushOptedOut } from "../hooks/useFCM";
import { Phone, ArrowRight, Shield, User, MapPin, Gift, Cake, UserPlus, MessageSquare } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Try to register an FCM token immediately after a successful login/register.
// This call happens inside the user-gesture handler so browsers allow the
// permission prompt. Silently ignores failures.
async function autoRegisterFcm() {
  try {
    if (isPushOptedOut()) return;   // клиент сам выключил уведомления в профиле
    const saveToken = async (token) => {
      await axios.post(`${API}/notifications/register`,
        { token, platform: isNativeApp() ? nativePlatform() : "web" }, { withCredentials: true });
      try { localStorage.setItem("angiolini_fcm_token", token); } catch { /* noop */ }
      return true;
    };
    // В Android-приложении токен выдаёт нативный FCM: системный диалог
    // показываем сразу после регистрации, пока действует жест пользователя.
    if (isNativeApp()) {
      await registerNativePush(saveToken);
      return;
    }
    const token = await requestFcmToken();
    if (token) await saveToken(token);
  } catch { /* noop */ }
}
const fadeUp = {
  initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 }, transition: { duration: 0.35 }
};

function formatPhone(digits) {
  if (!digits) return "";
  const d = digits.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 1) return `+${d}`;
  if (d.length <= 4) return `+${d[0]} (${d.slice(1)}`;
  if (d.length <= 7) return `+${d[0]} (${d.slice(1,4)}) ${d.slice(4)}`;
  if (d.length <= 9) return `+${d[0]} (${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`;
  return `+${d[0]} (${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7,9)}-${d.slice(9,11)}`;
}
import { useSeo } from "../lib/seo";

export default function Auth() {
  useSeo({ title: "Вход — ANGIOLINI VIP CLUB", noindex: true });
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLanguage();
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [delivery, setDelivery] = useState(null);
  // Default to SMS; existing saved choices still work with server-side fallback.
  const [channel, setChannel] = useState(() => {
    try { const saved = localStorage.getItem("auth_otp_channel"); return saved === "whatsapp" ? "whatsapp" : "sms"; } catch { return "sms"; }
  });
  useEffect(() => {
    try { localStorage.setItem("auth_otp_channel", channel); } catch { /* noop */ }
  }, [channel]);
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [referralCode, setReferralCode] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return (params.get("ref") || "").toUpperCase();
    } catch { return ""; }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Согласие на уведомления предлагаем уже отмеченным — клиент может снять
  // галочку до регистрации или отключить уведомления в профиле в любой момент.
  const [marketingConsent, setMarketingConsent] = useState(true);
  const [consent, setConsent] = useState(() => {
    // Pre-check consent when returning from Legal page with confirmation
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("consent") === "1";
    } catch { return false; }
  });

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!consent) {
      setError(t.auth.consentRequired);
      return;
    }
    setError(""); setDelivery(null); setLoading(true);
    try {
      const res = await axios.post(
        `${API}/auth/send-otp`,
        { phone, channel },
        { withCredentials: true },
      );
      const data = res.data || {};
      const actualChannel = data.channel || (data.sms_status ? "sms" : channel);
      const status = actualChannel === "sms" ? data.sms_status : data.wa_status;
      // Compatibility with older successful responses, but NEVER failed/mock/unknown.
      const accepted = data.accepted === true && data.delivery_status === "accepted"
        || data.accepted === undefined && ["sent", "accepted"].includes(status);
      if (!accepted || ["failed", "mock", "unknown"].includes(status)) {
        setError(t.auth.otpSendFailed);
        return;
      }
      setDelivery({ channel: actualChannel, fallback: !!data.fallback, accepted: true });
      setOtp(["", "", "", ""]);
      setStep("otp");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : detail?.message || t.auth.otpSendFailed);
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async (code) => {
    const c = code || otp.join("");
    if (c.length < 4) return;
    setError(""); setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/verify-otp`, { phone, code: c }, { withCredentials: true });
      if (res.data.status === "login") {
        login(res.data.user);
        // Auto-enable push for clients (admin/manager skip)
        if (!["admin","manager"].includes(res.data.user.role)) {
          autoRegisterFcm();
        }
        // Honor a "post signup intent" set by the public share page (e.g.
        // the "Try on online" CTA routes the client straight to the AR
        // fitting page instead of the home feed after login).
        let intent = "";
        try {
          intent = sessionStorage.getItem("post_signup_intent") || "";
          if (intent) sessionStorage.removeItem("post_signup_intent");
        } catch { /* noop */ }
        if (["admin","manager"].includes(res.data.user.role)) {
          navigate("/admin");
        } else if (intent === "tryon") {
          navigate("/app/tryon");
        } else {
          navigate("/app/home");
        }
      } else { setStep("register"); }
    } catch (err) {
      setError(err.response?.data?.detail || t.auth.wrongCode);
    } finally { setLoading(false); }
  };

  const handleOtpInput = (idx, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const newOtp = [...otp]; newOtp[idx] = digit; setOtp(newOtp);
    if (digit && idx < 3) document.getElementById(`otp-${idx+1}`)?.focus();
    if (newOtp.filter(Boolean).length === 4) {
      setTimeout(() => handleVerifyOtp(newOtp.join("")), 100);
    }
  };

  // Birth date must be dd.mm.yyyy with a plausible year (>=1900) and a real
  // calendar date. Same regex is enforced on the backend — never trust client.
  const isBirthDateValid = (s) => {
    const m = String(s || "").trim().match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (!m) return false;
    const [_, dd, mm, yyyy] = m;
    const d = parseInt(dd, 10), mo = parseInt(mm, 10), y = parseInt(yyyy, 10);
    if (y < 1900 || y > new Date().getFullYear()) return false;
    const dt = new Date(y, mo - 1, d);
    return dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === d;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError(t.auth.enterName); return; }
    if (!isBirthDateValid(birthDate)) {
      setError("Укажите дату рождения в формате ДД.ММ.ГГГГ — она обязательна для получения подарков от Angiolini.");
      return;
    }
    setError(""); setLoading(true);
    try {
      // Consume share attribution — set either by PublicShare/PublicProductPreview
      // (via sessionStorage) or by ?share=<token> on the URL. Attributes the
      // new client to the manager who created the share bundle.
      let shareToken = "";
      try { shareToken = sessionStorage.getItem("share_token") || ""; } catch { /* noop */ }
      if (!shareToken && typeof window !== "undefined") {
        shareToken = new URLSearchParams(window.location.search).get("share") || "";
      }
      const res = await axios.post(`${API}/auth/register`, {
        phone,
        name: name.trim(),
        city,
        birth_date: birthDate,
        referral_code: referralCode || undefined,
        share_token: shareToken || undefined,
        marketing_push_consent: marketingConsent,
      }, { withCredentials: true });
      try { sessionStorage.removeItem("share_token"); } catch { /* noop */ }
      login(res.data.user);
      autoRegisterFcm();
      // Try-On intent routes new users straight to the AR fitting page.
      let intent = "";
      try {
        intent = sessionStorage.getItem("post_signup_intent") || "";
        if (intent) sessionStorage.removeItem("post_signup_intent");
      } catch { /* noop */ }
      navigate(intent === "tryon" ? "/app/tryon" : "/app/home");
    } catch (err) {
      setError(err.response?.data?.detail || t.auth.registerError);
    } finally { setLoading(false); }
  };

  return (
    <div style={{ background: "linear-gradient(180deg,var(--ang-bg) 0%,var(--ang-surface-2) 100%)", minHeight: "100vh", display: "flex", flexDirection: "column", maxWidth: "430px", margin: "0 auto", position: "relative" }}>
      {/* Top language switcher */}
      <div style={{ position: "absolute", top: "20px", right: "20px", zIndex: 5 }}>
        <LanguageSwitcher dark={true} testIdPrefix="auth-language" />
      </div>

      {/* Logo (real brand) */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: "44px", paddingBottom: "26px" }}>
        <Logo size="lg" variant="dark" />
        <div style={{ width: "80px", height: "1px", background: "linear-gradient(90deg,transparent,var(--ang-gold),transparent)", margin: "6px 0 8px" }} />
        <div style={{ fontFamily: "Manrope", fontSize: "10px", letterSpacing: "0.4em", color: "var(--ang-gold-strong)", textTransform: "uppercase", fontWeight: 700 }}>VIP CLUB</div>
      </div>

      <AnimatePresence mode="wait">
        {step === "phone" && (
          <motion.div key="phone" {...fadeUp} style={{ flex: 1, padding: "0 24px" }}>
            <h1 style={{ fontFamily: "Cormorant Garamond", fontSize: "34px", fontWeight: 300, color: "var(--ang-text)", lineHeight: 1.2, marginBottom: "10px" }}>{t.auth.welcome}</h1>
            <p style={{ fontFamily: "Manrope", fontSize: "13px", color: "var(--ang-text-faint)", marginBottom: "20px", lineHeight: 1.6 }}>{t.auth.enterPhone}</p>

            {/* Welcome bonus banner */}
            <div data-testid="welcome-banner" style={{
              background: "linear-gradient(135deg, var(--ang-gold-soft), var(--ang-gold-soft))",
              border: "1px solid var(--ang-gold-line)",
              borderRadius: "8px", padding: "12px 14px", marginBottom: "24px",
              display: "flex", alignItems: "center", gap: "12px",
            }}>
              <Gift size={22} style={{ color: "var(--ang-gold)", flexShrink: 0 }} />
              <div>
                <div style={{ fontFamily: "Cormorant Garamond", fontSize: "16px", color: "var(--ang-gold-2)", fontWeight: 500 }}>{t.auth.welcomeBonusTitle}</div>
                <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-text-muted)", marginTop: "2px" }}>{t.auth.welcomeBonusSub}</div>
              </div>
            </div>
            <form data-testid="otp-request-form" onSubmit={handleSendOtp} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <div style={{ position: "relative" }}>
                <Phone size={15} style={{ position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", color: "var(--ang-gold)" }} />
                <input data-testid="phone-input" type="tel" value={formatPhone(phone)} disabled={loading}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 11))}
                  placeholder="+7 (___) ___-__-__"
                  style={{ width: "100%", background: "var(--ang-surface)", border: "1px solid var(--ang-border)", borderRadius: "2px", padding: "16px 16px 16px 44px", color: "var(--ang-text)", fontSize: "16px", fontFamily: "Manrope", outline: "none" }} />
              </div>
              {error && <p role="alert" data-testid="otp-request-error" style={{ color: "#ff6b6b", fontSize: "13px", fontFamily: "Manrope" }}>{error}</p>}

              {/* The server performs the only fallback, preserving one code/request. */}
              <div style={{ display: "flex", gap: "8px" }}>
                <ChannelPill active={channel === "sms"} disabled={loading} onClick={() => setChannel("sms")}
                  icon={<MessageSquare size={15} />} label="SMS" testid="channel-sms-btn" />
                <ChannelPill active={channel === "whatsapp"} disabled={loading} onClick={() => setChannel("whatsapp")}
                  icon={<WhatsAppIcon />} label="WhatsApp" testid="channel-whatsapp-btn" />
              </div>

              <label data-testid="consent-checkbox-label"
                style={{ display: "flex", alignItems: "flex-start", gap: "12px", cursor: "pointer",
                          padding: "14px", borderRadius: "6px",
                          background: consent ? "var(--ang-gold-soft)" : "var(--ang-border)",
                          border: `1px solid ${consent ? "var(--ang-gold)" : "var(--ang-gold-line)"}`,
                          transition: "background-color 0.2s, border-color 0.2s" }}>
                <input data-testid="consent-checkbox" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                  style={{ marginTop: "1px", width: "22px", height: "22px", accentColor: "var(--ang-gold)", cursor: "pointer", flexShrink: 0 }} />
                <span style={{ fontFamily: "Manrope", fontSize: "12.5px", color: consent ? "var(--ang-text-soft)" : "var(--ang-text-soft)", lineHeight: 1.55 }}>
                  {t.auth.consentPrefix}{" "}
                  <a href="/legal?tab=privacy" target="_blank" rel="noopener noreferrer" data-testid="link-privacy"
                    style={{ color: "var(--ang-gold-2)", textDecoration: "underline" }}>{t.auth.consentPersonalData}</a>
                  {" "}{t.auth.consentMiddle}{" "}
                  <a href="/legal?tab=terms" target="_blank" rel="noopener noreferrer" data-testid="link-terms"
                    style={{ color: "var(--ang-gold-2)", textDecoration: "underline" }}>{t.auth.consentLoyalty}</a>.
                </span>
              </label>

              {!consent && phone.length >= 4 && (
                <div data-testid="consent-hint"
                  style={{ fontFamily: "Manrope", fontSize: "11.5px", color: "var(--ang-gold-2)",
                            textAlign: "center", marginTop: "-4px" }}>
                  ↑ Поставьте галочку, чтобы получить код
                </div>
              )}

              <button data-testid="send-otp-btn" type="submit" disabled={loading || phone.length < 11 || !consent}
                style={{ background: "linear-gradient(135deg,var(--ang-gold),var(--ang-gold-2))", color: "var(--ang-bg)", padding: "16px", border: "none", borderRadius: "2px", cursor: "pointer", fontFamily: "Manrope", fontWeight: 700, fontSize: "11px", letterSpacing: "0.15em", textTransform: "uppercase", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", opacity: (phone.length < 11 || !consent) ? 0.5 : 1 }}>
                {loading ? t.auth.sending : <><span>{t.auth.getCode}</span><ArrowRight size={15} /></>}
              </button>
              {error && consent && phone.length === 11 && <button type="button" data-testid="otp-manual-entry-btn" disabled={loading}
                onClick={() => { setError(""); setDelivery({ channel: "manual", accepted: false }); setOtp(["", "", "", ""]); setStep("otp"); }}
                style={{ background: "none", border: "1px solid var(--ang-gold-line)", color: "var(--ang-gold-2)", padding: "12px", borderRadius: "4px", fontFamily: "Manrope", fontSize: "12px", cursor: "pointer" }}>
                {t.auth.otpManualEntry}
              </button>}
            </form>
            <div style={{ marginTop: "24px", textAlign: "center", display: "flex", flexDirection: "column", gap: "14px" }}>
              <button type="button" data-testid="continue-as-guest" onClick={() => navigate("/app/home")}
                style={{ background: "none", border: "none", color: "var(--ang-gold-2)", fontFamily: "Manrope",
                  fontSize: "12px", letterSpacing: "0.06em", textDecoration: "underline", cursor: "pointer" }}>
                Смотреть каталог без регистрации
              </button>
              <a href="/legal?tab=contacts" data-testid="link-contacts" style={{ fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-text-faint)", textDecoration: "none", letterSpacing: "0.06em" }}>
                {t.auth.contactsLink}
              </a>
            </div>
          </motion.div>
        )}

        {step === "otp" && (
          <motion.div key="otp" {...fadeUp} style={{ flex: 1, padding: "0 24px" }}>
            <h1 data-testid="otp-entry-heading" style={{ fontFamily: "Cormorant Garamond", fontSize: "34px", fontWeight: 300, color: "var(--ang-text)", marginBottom: "10px" }}>{t.auth.otpTitle}</h1>
            <p data-testid="otp-recipient-phone" style={{ fontFamily: "Manrope", fontSize: "13px", color: "var(--ang-text-muted)", marginBottom: "28px" }}>{t.auth.otpForNumber} {formatPhone(phone)}</p>
            {delivery?.accepted && (
              <div role="status" data-testid="otp-accepted-banner" style={{ background: "rgba(74,222,128,0.06)", border: "1px solid rgba(74,222,128,0.2)", borderRadius: "4px", padding: "12px 14px", marginBottom: "20px", fontFamily: "Manrope", fontSize: "12px", color: "rgba(74,222,128,0.9)", textAlign: "center" }}>
                {delivery.channel === "sms" ? t.auth.otpSmsAccepted : t.auth.otpWhatsappAccepted}
                {delivery.fallback && <p data-testid="otp-fallback-notice" style={{ marginTop: "8px" }}>{t.auth.otpSmsFallback}</p>}
              </div>
            )}
            {delivery?.channel === "manual" && (
              <div role="status" data-testid="otp-manual-notice" style={{ color: "var(--ang-gold-2)", fontFamily: "Manrope", fontSize: "12px", marginBottom: "20px", textAlign: "center" }}>
                {t.auth.otpManualNotice}
              </div>
            )}
            <div style={{ display: "flex", gap: "12px", justifyContent: "center", marginBottom: "24px" }}>
              {otp.map((digit, idx) => (
                <input key={idx} id={`otp-${idx}`} data-testid={`otp-input-${idx}`}
                  type="text" inputMode="numeric" maxLength={1} value={digit}
                  onChange={(e) => handleOtpInput(idx, e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Backspace" && !digit && idx > 0) document.getElementById(`otp-${idx-1}`)?.focus(); }}
                  style={{ width: "64px", height: "72px", background: "var(--ang-surface)", border: `1px solid ${digit ? "var(--ang-gold)" : "var(--ang-border)"}`, borderRadius: "2px", textAlign: "center", fontSize: "28px", fontFamily: "Cormorant Garamond", color: "var(--ang-text)", outline: "none" }} />
              ))}
            </div>
            {error && <p role="alert" data-testid="otp-verify-error" style={{ color: "#ff6b6b", fontSize: "13px", textAlign: "center", marginBottom: "16px" }}>{error}</p>}
            <button data-testid="verify-otp-btn" onClick={() => handleVerifyOtp()} disabled={loading || otp.join("").length < 4}
              style={{ width: "100%", background: "linear-gradient(135deg,var(--ang-gold),var(--ang-gold-2))", color: "var(--ang-bg)", padding: "16px", border: "none", borderRadius: "2px", cursor: "pointer", fontFamily: "Manrope", fontWeight: 700, fontSize: "11px", letterSpacing: "0.15em", textTransform: "uppercase", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", opacity: otp.join("").length < 4 ? 0.5 : 1 }}>
              <Shield size={15} />{loading ? t.auth.checking : t.auth.confirm}
            </button>
            <button data-testid="otp-change-phone-btn" disabled={loading} onClick={() => { setStep("phone"); setOtp(["","","",""]); setDelivery(null); setError(""); }}
              style={{ width: "100%", background: "none", border: "none", color: "var(--ang-text-faint)", fontFamily: "Manrope", fontSize: "13px", cursor: "pointer", marginTop: "16px", padding: "12px" }}>
              {t.auth.changePhone}
            </button>
          </motion.div>
        )}

        {step === "register" && (
          <motion.div key="register" {...fadeUp} style={{ flex: 1, padding: "0 24px" }}>
            <h1 style={{ fontFamily: "Cormorant Garamond", fontSize: "34px", fontWeight: 300, color: "var(--ang-text)", marginBottom: "10px" }}>{t.auth.createProfile}</h1>
            <p style={{ fontFamily: "Manrope", fontSize: "13px", color: "var(--ang-text-faint)", marginBottom: "24px" }}>{t.auth.welcome2}</p>

            {/* Welcome banner persists during registration */}
            <div style={{
              background: "linear-gradient(135deg, var(--ang-gold-soft), var(--ang-gold-soft))",
              border: "1px solid var(--ang-gold-line)",
              borderRadius: "8px", padding: "10px 12px", marginBottom: "18px",
              display: "flex", alignItems: "center", gap: "10px",
            }}>
              <Gift size={18} style={{ color: "var(--ang-gold)", flexShrink: 0 }} />
              <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "var(--ang-text-soft)" }}>
                {t.auth.regBonusPrefix} <b style={{ color: "var(--ang-gold-2)" }}>{t.auth.regBonusAmount}</b> {t.auth.regBonusSuffix}
              </div>
            </div>

            <form onSubmit={handleRegister} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              {[{ icon: User, value: name, set: setName, placeholder: t.auth.yourName, test: "register-name", type: "text" },
                { icon: MapPin, value: city, set: setCity, placeholder: t.auth.yourCity, test: "register-city", type: "text" }].map(({ icon: Icon, value, set, placeholder, test, type }) => (
                <div key={test} style={{ position: "relative" }}>
                  <Icon size={15} style={{ position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", color: "var(--ang-gold)" }} />
                  <input data-testid={test} type={type} value={value} onChange={(e) => set(e.target.value)} placeholder={placeholder}
                    style={{ width: "100%", background: "var(--ang-surface)", border: "1px solid var(--ang-border)", borderRadius: "2px", padding: "16px 16px 16px 44px", color: "var(--ang-text)", fontSize: "15px", fontFamily: "Manrope", outline: "none" }} />
                </div>
              ))}

              {/* Birth date is REQUIRED — used to send the Angiolini birthday
                  gift each year. The verification note deters false dates
                  (boutique cross-checks with government ID at first visit). */}
              <div>
                <div style={{
                  background: "var(--ang-gold-soft)",
                  border: "1px solid var(--ang-gold-line)",
                  borderRadius: "6px", padding: "10px 12px", marginBottom: "8px",
                  fontFamily: "Manrope", fontSize: "11.5px", color: "var(--ang-text-soft)", lineHeight: 1.5,
                }}>
                  🎁 <b style={{ color: "var(--ang-gold-2)" }}>Каждый год в свой день рождения</b> вы получаете персональный подарок от Angiolini.
                  Поэтому укажите <b>точную дату</b> — при первом визите в бутик мы попросим удостоверение личности для подтверждения.
                </div>
                <div style={{ position: "relative" }}>
                  <Cake size={15} style={{ position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", color: "var(--ang-gold)" }} />
                  <input data-testid="register-birth-date" type="text" value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                    placeholder="Дата рождения (ДД.ММ.ГГГГ) *"
                    style={{
                      width: "100%", background: "var(--ang-surface)",
                      border: `1px solid ${birthDate && !isBirthDateValid(birthDate) ? "rgba(255,107,107,0.5)" : "var(--ang-border)"}`,
                      borderRadius: "2px", padding: "16px 16px 16px 44px",
                      color: "var(--ang-text)", fontSize: "15px", fontFamily: "Manrope", outline: "none",
                    }} />
                </div>
              </div>

              {[{ icon: UserPlus, value: referralCode, set: (v) => setReferralCode(v.toUpperCase()), placeholder: t.auth.referralPlaceholder, test: "register-referral", type: "text" }].map(({ icon: Icon, value, set, placeholder, test, type }) => (
                <div key={test} style={{ position: "relative" }}>
                  <Icon size={15} style={{ position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", color: "var(--ang-gold)" }} />
                  <input data-testid={test} type={type} value={value} onChange={(e) => set(e.target.value)} placeholder={placeholder}
                    style={{ width: "100%", background: "var(--ang-surface)", border: "1px solid var(--ang-border)", borderRadius: "2px", padding: "16px 16px 16px 44px", color: "var(--ang-text)", fontSize: "15px", fontFamily: "Manrope", outline: "none" }} />
                </div>
              ))}
              {error && <p role="alert" data-testid="register-error" style={{ color: "#ff6b6b", fontSize: "13px" }}>{error}</p>}

              {/* Необязательное согласие на рекламные push — отдельно от
                  обязательного принятия политики конфиденциальности */}
              <label data-testid="marketing-consent-checkbox-label"
                style={{ display: "flex", gap: "12px", alignItems: "flex-start", cursor: "pointer",
                  background: marketingConsent ? "var(--ang-gold-soft)" : "var(--ang-surface)",
                  border: `1px solid ${marketingConsent ? "var(--ang-gold-line)" : "var(--ang-border)"}`,
                  borderRadius: "2px", padding: "14px 16px",
                  transition: "background-color .25s ease, border-color .25s ease" }}>
                <input data-testid="marketing-consent-checkbox" type="checkbox" checked={marketingConsent}
                  onChange={(e) => setMarketingConsent(e.target.checked)}
                  style={{ width: "18px", height: "18px", accentColor: "var(--ang-gold)", marginTop: "1px", flexShrink: 0 }} />
                <span>
                  <span style={{ fontFamily: "Manrope", fontSize: "12.5px", color: "var(--ang-text-soft)", lineHeight: 1.55 }}>
                    Я согласен(на) получать уведомления ANGIOLINI о новых коллекциях, акциях,
                    VIP-предложениях, новостях и событиях бутика
                  </span>
                  <span style={{ display: "block", fontFamily: "Manrope", fontSize: "11px",
                    color: "var(--ang-text-muted)", marginTop: "6px", lineHeight: 1.5 }}>
                    Снять галочку можно сейчас, а отключить уведомления — в любой момент в профиле.{" "}
                    <a href="/legal?tab=privacy" target="_blank" rel="noopener noreferrer"
                      data-testid="marketing-consent-privacy-link"
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: "var(--ang-gold-2)", textDecoration: "underline" }}>
                      Политика конфиденциальности
                    </a>
                  </span>
                </span>
              </label>

              <button data-testid="register-submit" type="submit"
                disabled={loading || !name.trim() || !isBirthDateValid(birthDate)}
                style={{ background: "linear-gradient(135deg,var(--ang-gold),var(--ang-gold-2))", color: "var(--ang-bg)", padding: "16px", border: "none", borderRadius: "2px", cursor: "pointer", fontFamily: "Manrope", fontWeight: 700, fontSize: "11px", letterSpacing: "0.15em", textTransform: "uppercase", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", opacity: (!name.trim() || !isBirthDateValid(birthDate)) ? 0.5 : 1, marginTop: "8px" }}>
                {loading ? t.auth.creating : <><span>{t.auth.joinClub}</span><ArrowRight size={15} /></>}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ChannelPill({ active, disabled, onClick, icon, label, testid }) {
  return (
    <button data-testid={testid} type="button" onClick={onClick} disabled={disabled} aria-pressed={active}
      style={{
        flex: 1,
        background: active ? "linear-gradient(135deg,var(--ang-gold),var(--ang-gold-2))" : "var(--ang-surface)",
        color: active ? "var(--ang-bg)" : "var(--ang-text-soft)",
        border: `1px solid ${active ? "var(--ang-gold)" : "var(--ang-border)"}`,
        borderRadius: "2px", padding: "12px", cursor: "pointer",
        fontFamily: "Manrope", fontWeight: active ? 700 : 500, fontSize: "12px",
        letterSpacing: "0.08em", textTransform: "uppercase",
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "8px",
      }}>
      {icon}
      <span>{label}</span>
    </button>
  );
}

// Inline WhatsApp brand mark — official green (#25D366) when the pill is
// inactive so it reads as "the WhatsApp channel", monochrome dark when the
// pill turns golden so it stays legible against the gradient background.
function WhatsAppIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
      <path d="M16.001 3C9.373 3 4 8.373 4 15c0 2.407.703 4.65 1.917 6.545L4 29l7.706-1.878A11.939 11.939 0 0 0 16 27c6.627 0 12-5.373 12-12S22.628 3 16.001 3zm0 21.6c-1.71 0-3.36-.4-4.83-1.15l-.345-.176-4.28 1.042 1.062-4.171-.226-.36A9.582 9.582 0 0 1 6.4 15c0-5.293 4.308-9.6 9.6-9.6 5.293 0 9.6 4.307 9.6 9.6 0 5.293-4.307 9.6-9.599 9.6zm5.269-6.984c-.289-.144-1.71-.844-1.975-.94-.264-.096-.457-.144-.65.144-.194.289-.746.94-.916 1.133-.169.194-.337.216-.626.072-.288-.144-1.218-.449-2.319-1.431-.858-.766-1.437-1.712-1.605-2-.169-.289-.018-.446.127-.59.13-.13.288-.337.433-.505.144-.169.192-.289.288-.481.096-.194.048-.361-.024-.505-.072-.144-.65-1.567-.89-2.146-.234-.564-.472-.487-.65-.496-.169-.008-.361-.01-.554-.01a1.065 1.065 0 0 0-.77.361c-.264.289-1.01.987-1.01 2.406 0 1.42 1.035 2.79 1.18 2.984.144.194 2.036 3.107 4.933 4.357.69.298 1.228.475 1.647.608.692.22 1.323.19 1.822.115.556-.083 1.71-.699 1.951-1.373.24-.674.24-1.253.169-1.373-.072-.12-.264-.192-.552-.336z" />
    </svg>
  );
}
